import { Fragment } from "react";
import { Spin } from "antd";
import type { LineConfig } from "./LineChartCard";
import styles from "./HeatmapChartCard.module.css";

export interface HeatmapChartCardProps {
  title: string;
  subtitle?: string;
  data: Record<string, string | number>[];
  xKey: string;
  series: LineConfig[];
  rowAxisLabel?: string;
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
  valueSuffix?: string;
  sortSeriesByConcentration?: boolean;
  valueColorScale?: (value: number, minValue: number, maxValue: number) => {
    backgroundColor: string;
    color?: string;
  };
  legendLabels?: {
    low: string;
    mid?: string;
    high: string;
  };
  legendScaleStyle?: string;
  rowMeta?: Record<string, string>;
  columnMeta?: Record<string, string>;
  formatCellValue?: (
    value: number,
    context: { rowKey: string; columnKey: string }
  ) => { primary: string; secondary?: string };
}

/** Escala per defecte: intensitat (concentració), sense semàfor verd/vermell */
const LOW_COLOR = "#F8FAFC";
const HIGH_COLOR = "#2563EB";

const clamp = (value: number) => Math.min(1, Math.max(0, value));

const hexToRgb = (hex: string) => {
  const normalized = hex.replace("#", "");
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const mixColor = (from: string, to: string, ratio: number) => {
  const start = hexToRgb(from);
  const end = hexToRgb(to);

  const r = Math.round(start.r + (end.r - start.r) * ratio);
  const g = Math.round(start.g + (end.g - start.g) * ratio);
  const b = Math.round(start.b + (end.b - start.b) * ratio);

  return `rgb(${r}, ${g}, ${b})`;
};

const formatValue = (value: number, suffix: string) => {
  const formattedValue = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(1).replace(/\.0$/, "");

  return `${formattedValue}${suffix}`;
};

const getNumericValue = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getAverageValue = (
  data: Record<string, string | number>[],
  key: string
) => {
  const values = data
    .map((point) => getNumericValue(point[key]))
    .filter((value): value is number => value !== null);

  if (values.length === 0) return -Infinity;

  return values.reduce((total, value) => total + value, 0) / values.length;
};

const HeatmapChartCard = ({
  title,
  subtitle,
  data,
  xKey,
  series,
  rowAxisLabel = "Àmbit",
  loading = false,
  error = null,
  headerExtra,
  valueSuffix = "%",
  sortSeriesByConcentration = false,
  valueColorScale,
  legendLabels,
  legendScaleStyle,
  rowMeta,
  columnMeta,
  formatCellValue,
}: HeatmapChartCardProps) => {
  const orderedSeries = sortSeriesByConcentration
    ? [...series].sort((a, b) => getAverageValue(data, b.key) - getAverageValue(data, a.key))
    : series;
  const values = data.flatMap((point) =>
    orderedSeries
      .map(({ key }) => getNumericValue(point[key]))
      .filter((value): value is number => value !== null)
  );
  const minValue = values.length > 0 ? Math.min(...values) : 0;
  const maxValue = values.length > 0 ? Math.max(...values) : 0;
  const hasRange = maxValue > minValue;
  const gridTemplateColumns = `minmax(180px, 1.35fr) repeat(${data.length}, minmax(84px, 1fr))`;

  return (
    <div className={styles.card} data-chart-card>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>{title}</h2>
          {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        </div>
        {headerExtra}
      </div>

      {loading && (
        <div className={styles.centered}>
          <Spin size="small" /> Carregant dades…
        </div>
      )}
      {!loading && error && (
        <div className={styles.centered}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
      {!loading && !error && (data.length === 0 || orderedSeries.length === 0) && (
        <div className={styles.centered}>
          <p className={styles.emptyMessage}>Sense dades disponibles</p>
        </div>
      )}
      {!loading && !error && data.length > 0 && orderedSeries.length > 0 && (
        <>
          <div className={styles.heatmapArea}>
            <div
              className={styles.grid}
              style={{ gridTemplateColumns }}
              role="table"
              aria-label={title}
            >
              <div className={styles.axisCell} role="columnheader">
                {rowAxisLabel}
              </div>
              {data.map((point) => (
                <div
                  key={String(point[xKey])}
                  className={styles.columnHeader}
                  role="columnheader"
                >
                  <div className={styles.columnHeaderContent}>
                    <span>{point[xKey]}</span>
                    {columnMeta?.[String(point[xKey])] ? (
                      <span className={styles.columnMeta}>{columnMeta[String(point[xKey])]}</span>
                    ) : null}
                  </div>
                </div>
              ))}

              {orderedSeries.map(({ key, label }) => (
                <Fragment key={key}>
                  <div key={`${key}-label`} className={styles.rowHeader} role="rowheader">
                    <div className={styles.rowHeaderContent}>
                      <span>{label}</span>
                      {rowMeta?.[key] ? <span className={styles.rowMeta}>{rowMeta[key]}</span> : null}
                    </div>
                  </div>
                  {data.map((point) => {
                    const value = getNumericValue(point[key]);
                    const ratio = value === null
                      ? 0
                      : hasRange
                        ? clamp((value - minValue) / (maxValue - minValue))
                        : 0.5;
                    const defaultBackgroundColor = value === null
                      ? "#F8FAFC"
                      : mixColor(LOW_COLOR, HIGH_COLOR, ratio);
                    const defaultColor = ratio > 0.58 ? "#FFFFFF" : "#252B33";
                    const customScale = value !== null && valueColorScale
                      ? valueColorScale(value, minValue, maxValue)
                      : null;
                    const backgroundColor = customScale?.backgroundColor ?? defaultBackgroundColor;
                    const color = customScale?.color ?? defaultColor;
                    const formattedCell = value !== null && formatCellValue
                      ? formatCellValue(value, {
                          rowKey: key,
                          columnKey: String(point[xKey]),
                        })
                      : null;
                    const tooltipValue = value === null
                      ? "sense dades"
                      : formattedCell?.secondary
                        ? `${formattedCell.primary} · ${formattedCell.secondary}`
                        : formatValue(value, valueSuffix);

                    return (
                      <div
                        key={`${key}-${point[xKey]}`}
                        className={styles.cell}
                        style={{ backgroundColor, color }}
                        role="cell"
                        title={`${label}, ${point[xKey]}: ${
                          tooltipValue
                        }`}
                        aria-label={`${label}, ${point[xKey]}: ${
                          tooltipValue
                        }`}
                      >
                        {value === null ? (
                          "-"
                        ) : (
                          <div className={styles.cellContent}>
                            <span className={styles.cellPrimary}>
                              {formattedCell?.primary ?? formatValue(value, valueSuffix)}
                            </span>
                            {formattedCell?.secondary ? (
                              <span className={styles.cellSecondary}>{formattedCell.secondary}</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
          <div className={styles.legend}>
            <span>{legendLabels?.low ?? `Baix ${formatValue(minValue, valueSuffix)}`}</span>
            <span
              className={styles.legendScale}
              style={legendScaleStyle ? { background: legendScaleStyle } : undefined}
            />
            {legendLabels?.mid ? <span>{legendLabels.mid}</span> : null}
            <span>{legendLabels?.high ?? `Alt ${formatValue(maxValue, valueSuffix)}`}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default HeatmapChartCard;
